/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export default class TillsController extends Controller {
  @service
  router;

  @action
  setTillCode(value) {
    this.tillCode = value;
  }

  @action
  setTillDescription(value) {
    this.tillDescription = value;
  }

  @action
  setTillIsActive(value) {
    this.tillIsActive = value;
  }

  // Services
  @service
  store;

  @tracked tillId = null;
  @tracked
  tillCode = '';
  @tracked
  tillDescription = '';
  @tracked
  tillIsActive = true;
  @tracked
  tillDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked
  deleteTillDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  errorMessage = '';

  @tracked
  tillLoaded;

  @tracked
  tillSessionCount;

  @computed('model', 'tillId')
  get codeValidations() {
    if (!isEmpty(this.tillId)) {
      // Editing an existing Till.  Don't try to validate.
      return [];
    }

    return [
      {
        message:
          'The specified code is already in use.  Till codes must be unique.',
        validate: (code) => {
          return !isEmpty(code) && isEmpty(findBy(this.model, 'code', code));
        },
      },
    ];
  }

  // toggleDialog opens/closes the Till dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('tillDialogOpen');
    if (!this.tillDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleTillDialog() {
    this.toggleDialog();
  }

  @action
  editTill(id) {
    this.store.findRecord('finance/till', id).then((till) => {
      this.tillCode = till.code;
      this.tillDescription = till.description;
      this.tillIsActive = till.isActive;
    });
    this.tillId = id;
    this.toggleDialog();
  }

  @action
  saveTill() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.tillId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('finance/till', {
          code: this.tillCode,
          description: this.tillDescription,
          isActive: true,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.tillId;
        this.store.findRecord('finance/till', id).then((till) => {
          till.setProperties({
            code: this.tillCode,
            description: this.tillDescription,
            isActive: this.tillIsActive,
          });

          resolve(till);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('admin.finance.banking.tills');
          }

          this.tillDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteTill(id) {
    this.errorMessage = '';
    this.tillLoaded = false;
    hash({
      till: this.store.findRecord('finance/till', id),
      tillSessions: this.store.query('finance/till-session', { tillId: id }),
    }).then((data) => {
      this.tillCode = data.till.get('code');
      this.tillDescription = data.till.get('description');
      this.tillSessionCount = data.tillSessions.length;
      this.tillLoaded = true;
    });
    this.tillId = id;
    this.deleteTillDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let record = this.store.peekRecord('finance/till', this.tillId);

        // Delete record (locally + pushed to server).
        return record
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            record.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteTillDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    this.tillId = null;
    this.tillCode = '';
    this.tillDescription = '';
    this.tillIsActive = true;
  }
}
